import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';

import { GridLegacy as Grid } from '@mui/material';

import {
  VisitsTabs,
  DepartmentSelect,
  ReferringOrganizationSelect,
  ProviderLabelSelect,
  MonthlyVisitsBarChart,
  ReferringAffiliationOverTimeChart,
  TopReferralsInTable,
  PayerPieChart,
  VisitTypePieChart,
  ReferringAffiliationPieChart,
  DateRangeSelector,
} from 'components';

import { MarketsUtil } from 'utils';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  // Two rows: who was seen (Department, Provider Label) over who referred it
  // (Referring Organization, Referring Provider Label). Each row flows on its own, so the
  // selects sit next to each other rather than in aligned columns.
  dropdowns: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  dropdownRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    rowGap: theme.spacing(2),
  },
});

class VisitsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      department: null,
      referringOrganization: null,
      // Label of the provider who received the visit, and of the provider who referred
      // it. Either, both or neither (All) can be set.
      providerLabelId: null,
      referrerLabelId: null,
      query: {
        where: {},
        include: [
          {
            association: 'taxonomy',
          },
        ],
        order: [['id', 'DESC']],
        limit: 25,
        offset: 0,
      },
    };
  }

  render() {
    const { classes } = this.props;

    return (
      <>
        <VisitsTabs tab='visits' />

        <div className={classes.container}>
          <div className={classes.filters}>
            <div className={classes.dropdowns}>
              <div className={classes.dropdownRow}>
                <DepartmentSelect
                  value={this.state.department}
                  onChange={this.onDepartmentChange}
                />

                <ProviderLabelSelect
                  label='Provider Label'
                  value={this.state.providerLabelId}
                  onChange={this.onProviderLabelChange}
                />
              </div>

              <div className={classes.dropdownRow}>
                <ReferringOrganizationSelect
                  value={this.state.referringOrganization}
                  onChange={this.onReferringOrganizationChange}
                />

                <ProviderLabelSelect
                  label='Referring Provider Label'
                  value={this.state.referrerLabelId}
                  onChange={this.onReferrerLabelChange}
                />
              </div>
            </div>

            <DateRangeSelector />
          </div>

          <MonthlyVisitsBarChart
            department={this.state.department}
            referringOrganization={this.state.referringOrganization}
            providerLabelId={this.state.providerLabelId}
            referrerLabelId={this.state.referrerLabelId}
            clientId={this.props.clientId}
          />

          <ReferringAffiliationOverTimeChart
            side='referrer'
            department={this.state.department}
            referringOrganization={this.state.referringOrganization}
            providerLabelId={this.state.providerLabelId}
            referrerLabelId={this.state.referrerLabelId}
            clientId={this.props.clientId}
          />

          {/* Referred Provider Affiliation (side='provider') is hidden for now and not
              mounted, so its report isn't requested. Visits are the client's own
              encounters, so the seen provider should be ~100% affiliated; until the
              affiliation flags are trusted, it reads as leakage that isn't there. */}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TopReferralsInTable
                clientId={this.props.clientId}
                department={this.state.department}
                referringOrganization={this.state.referringOrganization}
                providerLabelId={this.state.providerLabelId}
                referrerLabelId={this.state.referrerLabelId}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PayerPieChart
                clientId={this.props.clientId}
                department={this.state.department}
                referringOrganization={this.state.referringOrganization}
                providerLabelId={this.state.providerLabelId}
                referrerLabelId={this.state.referrerLabelId}
              />
              <VisitTypePieChart
                clientId={this.props.clientId}
                department={this.state.department}
                referringOrganization={this.state.referringOrganization}
                providerLabelId={this.state.providerLabelId}
                referrerLabelId={this.state.referrerLabelId}
              />
              <ReferringAffiliationPieChart
                clientId={this.props.clientId}
                department={this.state.department}
                referringOrganization={this.state.referringOrganization}
                providerLabelId={this.state.providerLabelId}
                referrerLabelId={this.state.referrerLabelId}
              />
            </Grid>
            <div style={{ minHeight: 200 }}>&nbsp;</div>
          </Grid>
        </div>
      </>
    );
  }

  onDepartmentChange = (newDepartment) => {
    this.setState({
      department: newDepartment,
    });
  };

  onReferringOrganizationChange = (newOrg) => {
    this.setState({
      referringOrganization: newOrg,
    });
  };

  onProviderLabelChange = (newLabelId) => {
    this.setState({
      providerLabelId: newLabelId,
    });
  };

  onReferrerLabelChange = (newLabelId) => {
    this.setState({
      referrerLabelId: newLabelId,
    });
  };
}

VisitsPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object,
  clientId: PropTypes.number.isRequired,
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
    market: MarketsUtil.getSelectedMarket(state),
  };
}

const styled = withStyles(styles)(VisitsPage);
const connected = connect(mapStateToProps)(styled);
export { connected as VisitsPage };
